import React, { useMemo, useState, useEffect, useCallback } from 'react';
import './RiderOverviewModal.css';

const RACE_ABBREVIATIONS = {
    'Omloop het Nieuwsblad': 'OHN',
    'Kuurne-Brussel-Kuurne': 'KBK',
    'Strade Bianche': 'SB',
    'Paris-Nice': 'PN',
    'Tirreno-Adriatico': 'TA',
    'Milano-Sanremo': 'MSR',
    'Ronde van Brugge': 'RvB',
    'E3 Saxo Classic': 'E3',
    'In Flanders Fields': 'IFF',
    'Dwars door Vlaanderen': 'DDV',
    'Ronde van Vlaanderen': 'RVV',
    'Scheldeprijs': 'SP',
    'Paris-Roubaix': 'PR',
    'Brabantse Pijl': 'BP',
    'Amstel Gold Race': 'AGR',
    'Waalse Pijl': 'WP',
    'Luik-Bastenaken-Luik': 'LBL'
};

// RACES_ORDER removed, races will be passed down and mapped
const getRaceAbbr = (race) => {
    if (RACE_ABBREVIATIONS[race]) return RACE_ABBREVIATIONS[race];
    if (race === 'Eindklassement') return 'EK';
    if (race.startsWith('Etappe ')) return race.replace('Etappe ', '');
    return race;
};

// Memoized row component to prevent unnecessary re-renders
const RiderRow = React.memo(({ rider, onRiderClick, races, showFavoritesOnly }) => {
    return (
        <tr className={`${rider.isSelected ? 'selected-rider-row' : ''} ${rider.isInvalid ? 'invalid-rider-row' : ''}`}>
            <td
                className="sticky-col name-cell clickable-name"
                title="Klik voor historie"
                onClick={() => onRiderClick(rider.fullName, rider.riderId)}
            >
                {rider.surname} <span className={`rider-price-inline ${rider.price === 10000 ? 'price-red' : ''}`}>({(rider.price / 1000000).toFixed(2)}M)</span>
                {rider.isFavorite && <span className="favorite-star" title="Favoriet">⭐</span>}
            </td>
            {races.map(race => {
                const isChecked = showFavoritesOnly 
                    ? rider.favRacesSet.has(race) 
                    : rider.racesSet.has(race);
                    
                return (
                    <td key={race} className="race-cell">
                        {isChecked && (
                            <span className="check-mark">✓</span>
                        )}
                    </td>
                );
            })}
        </tr>
    );
});

function RiderOverviewModal({
    isOpen,
    onClose,
    allRiders,
    competitions,
    apiUrl,
    sortBy,
    setSortBy,
    activeRaceSorts,
    setActiveRaceSorts,
    showFavoritesFirst,
    setShowFavoritesFirst,
    favoriteRiders,
    selectedRiders,
    onShowHistory,
    modalId,
    races,
    showFavoritesOnly = false,
    gameMode
}) {
    // Local state for search resets automatically when modal unmounts
    const [inputValue, setInputValue] = useState('');
    const [searchTerm, setSearchTerm] = useState('');

    // Debounce search input
    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchTerm(inputValue);
        }, 300);
        return () => clearTimeout(timer);
    }, [inputValue]);

    // Tier 1: Pre-process data (Only when allRiders, favoriteRiders or selectedRiders change)
    const baseRiderData = useMemo(() => {
        if (!allRiders) return [];

        // Pre-calculate sets for fast lookup scoped to the current game's races
        const favNames = new Set();
        const favRacesByRider = {}; // { riderName: Set(races) }
        const relevantRaces = new Set(races || []);
        
        if (favoriteRiders) {
            Object.entries(favoriteRiders).forEach(([race, raceList]) => {
                if (!relevantRaces.has(race)) return; // Scope check
                
                if (Array.isArray(raceList)) {
                    raceList.forEach(f => {
                        if (!f.rider) return;
                        const nameKey = f.rider.trim().toLowerCase();
                        favNames.add(nameKey);
                        if (!favRacesByRider[nameKey]) favRacesByRider[nameKey] = new Set();
                        favRacesByRider[nameKey].add(race);
                    });
                }
            });
        }
        const selNames = new Set((selectedRiders || []).map(r => r.trim().toLowerCase()));

        return Object.entries(allRiders)
            .filter(([name, data]) => {
                // Alleen filteren in Giro-modus: verberg renners zonder startlijst-deelname
                if (gameMode !== 'klassiekers') {
                    return (data.races || []).length > 0;
                }
                return true;
            })
            .map(([name, data]) => {
            const nameTrimmed = name.trim();
            const nameLower = nameTrimmed.toLowerCase();
            const nameParts = nameTrimmed.split(' ');
            const surname = nameParts.length > 1 ? nameParts.slice(0, -1).join(' ') : nameTrimmed;
            const racesSet = new Set(data.races || []);

            return {
                fullName: nameTrimmed,
                surname: surname,
                price: data.price || 500000,
                racesRaw: data.races || [], // Keep for count
                racesSet: racesSet, // O(1) lookup
                favRacesSet: favRacesByRider[nameLower] || new Set(), // Favorites only (scoped)
                raceCount: (data.races || []).length,
                riderId: data.RiderId || null,
                teamname: data.teamname || '',
                team_abbr: data.team_abbr || '',
                isFavorite: favNames.has(nameLower),
                isSelected: selNames.has(nameLower),
                isInvalid: data.isInvalid || false,
                klassement: data.klassement || 0,
                klimmen: data.klimmen || 0,
                tijdrijden: data.tijdrijden || 0,
                sprinten: data.sprinten || 0,
                heuvels: data.heuvels || 0,
                punchen: data.punchen || 0,
                kasseien: data.kasseien || 0,
                birth: data.birth || ''
            };
        });
    }, [allRiders, favoriteRiders, selectedRiders, races, gameMode]);

    // Tier 2: Filter and Sort
    const processedRiders = useMemo(() => {
        let filtered = baseRiderData;

        if (searchTerm.trim()) {
            const lowerSearch = searchTerm.toLowerCase();
            filtered = baseRiderData.filter(r => 
                r.fullName.toLowerCase().includes(lowerSearch) ||
                (r.teamname && r.teamname.toLowerCase().includes(lowerSearch)) ||
                (r.team_abbr && r.team_abbr.toLowerCase().includes(lowerSearch))
            );
        }

        if (sortBy === 'youth') {
            // No filtering here as per user request: "overzichtstabel wel alle renners getoont worden"
        }

        // Create a copy for sorting
        const result = [...filtered];

        result.sort((a, b) => {
            // Absolute Primary sort: Favorites (if toggled)
            if (showFavoritesFirst) {
                if (a.isFavorite !== b.isFavorite) return b.isFavorite - a.isFavorite;
            }

            // Primary sort: Hierarchical race sorting
            if (activeRaceSorts.length > 0) {
                // Optimization: use the pre-processed Set for faster count calculation
                let matchCountA = 0;
                let matchCountB = 0;
                for (const r of activeRaceSorts) {
                    if (showFavoritesOnly) {
                        if (a.favRacesSet.has(r)) matchCountA++;
                        if (b.favRacesSet.has(r)) matchCountB++;
                    } else {
                        if (a.racesSet.has(r)) matchCountA++;
                        if (b.racesSet.has(r)) matchCountB++;
                    }
                }

                if (matchCountA !== matchCountB) return matchCountB - matchCountA;

                // Priority tie-breaker
                for (let i = 0; i < activeRaceSorts.length; i++) {
                    const race = activeRaceSorts[i];
                    const aHas = showFavoritesOnly ? a.favRacesSet.has(race) : a.racesSet.has(race);
                    const bHas = showFavoritesOnly ? b.favRacesSet.has(race) : b.racesSet.has(race);
                    if (aHas !== bHas) return bHas - aHas;
                }
            }

            // Secondary sort: Global sort
            if (sortBy === 'price') {
                if (b.price !== a.price) return b.price - a.price;
            } else if (sortBy === 'races') {
                const countA = showFavoritesOnly ? a.favRacesSet.size : a.raceCount;
                const countB = showFavoritesOnly ? b.favRacesSet.size : b.raceCount;
                if (countB !== countA) return countB - countA;
                if (b.price !== a.price) return b.price - a.price;
            } else if (sortBy === 'youth') {
                // Tier 0: Youth status (born on or after 2001-01-01)
                const isYouthA = a.birth && a.birth >= '2001-01-01' ? 1 : 0;
                const isYouthB = b.birth && b.birth >= '2001-01-01' ? 1 : 0;
                if (isYouthA !== isYouthB) return isYouthB - isYouthA;

                // Tier 1: Klassement quality
                if ((b.klassement || 0) !== (a.klassement || 0)) return (b.klassement || 0) - (a.klassement || 0);
                // Tier 2: Klimmen quality
                if ((b.klimmen || 0) !== (a.klimmen || 0)) return (b.klimmen || 0) - (a.klimmen || 0);
                // Tier 3: Heuvels quality
                if ((b.heuvels || 0) !== (a.heuvels || 0)) return (b.heuvels || 0) - (a.heuvels || 0);
                // Tier 4: Price
                if (b.price !== a.price) return b.price - a.price;
                // Tier 5: Age (Youngest first)
                const valA = a.birth || '1900-01-01';
                const valB = b.birth || '1900-01-01';
                return valB.localeCompare(valA);
            } else {
                // Skill sorting
                if (b[sortBy] !== a[sortBy]) return b[sortBy] - a[sortBy];
                if (b.price !== a.price) return b.price - a.price;
            }
            
            // Tie-breaker 2: Name (Default fallback)
            return a.surname.localeCompare(b.surname);
        });

        return result;
    }, [baseRiderData, searchTerm, sortBy, activeRaceSorts, showFavoritesFirst, showFavoritesOnly]);

    const handleRaceHeaderClick = useCallback((race) => {
        setActiveRaceSorts(prev =>
            prev.includes(race)
                ? prev.filter(r => r !== race)
                : [...prev, race]
        );
    }, [setActiveRaceSorts]);

    const handleInternalClose = useCallback((e) => {
        if (e) {
            // Prevent synthesized click events on mobile
            if (e.cancelable && (e.type === 'touchend' || e.type === 'touchstart' || e.type === 'pointerdown')) {
                e.preventDefault();
            }
            e.stopPropagation();
        }
        onClose();
    }, [onClose]);

    const handleRiderClick = useCallback((fullName, riderId) => {
        onShowHistory({ name: fullName, riderId });
    }, [onShowHistory]);

    if (!isOpen) return null;

    return (
        <div 
            className="overview-overlay" 
            onClick={handleInternalClose}
            onTouchEnd={handleInternalClose}
        >
            <div 
                className="overview-modal modal-box" 
                data-modal-id={modalId} 
                onClick={e => e.stopPropagation()}
                onTouchEnd={e => e.stopPropagation()}
            >
                <div className="overview-header">
                    <h2>{gameMode === 'klassiekers' ? "Programma's" : "Overzicht"}</h2>
                    <button 
                        className="overview-close-btn" 
                        onClick={handleInternalClose}
                        onTouchEnd={handleInternalClose}
                    >×</button>
                </div>

                <div className="overview-controls">
                    <div className="overview-search-box">
                        <input
                            type="text"
                            placeholder="Zoek renner of ploeg..."
                            className="overview-search"
                            value={inputValue}
                            onChange={e => setInputValue(e.target.value)}
                        />
                        {inputValue && (
                            <button
                                className="overview-search-clear-button"
                                onClick={() => setInputValue('')}
                                title="Zoekopdracht wissen"
                            >
                                ✕
                            </button>
                        )}
                    </div>
                    <div className="overview-sort-group">
                        <div className="overview-sort">
                            <span>Sorteer op:</span>
                            <select 
                                className="overview-sort-select"
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                            >
                                <option value="price">Prijs</option>
                                <option value="races">
                                    {showFavoritesOnly ? 'Aantal favoriet' : (gameMode === 'klassiekers' ? 'Aantal wedstrijden' : 'Aantal etappes')}
                                </option>
                                {gameMode !== 'klassiekers' && (
                                    <>
                                        <option value="klassement">Klassement</option>
                                        <option value="klimmen">Klimmen</option>
                                        <option value="tijdrijden">Tijdrijden</option>
                                        <option value="sprinten">Sprinten</option>
                                        <option value="heuvels">Heuvels</option>
                                        <option value="punchen">Punchen</option>
                                        <option value="kasseien">Kasseien</option>
                                        <option value="youth">Jongeren</option>
                                    </>
                                )}
                            </select>
                        </div>
                        <button
                            className={`sort-btn fav-sort-btn ${showFavoritesFirst ? 'active' : ''}`}
                            onClick={() => setShowFavoritesFirst(!showFavoritesFirst)}
                            title="Favorieten bovenaan zetten"
                        >
                            Favorieten bovenaan
                        </button>
                    </div>
                </div>

                <div className="overview-table-container">
                    <table className="overview-table">
                        <thead>
                            <tr>
                                <th className="sticky-col name-header">Renner</th>
                                {races.map(race => (
                                    <th
                                        key={race}
                                        title={race}
                                        className={activeRaceSorts.includes(race) ? 'active-sort' : ''}
                                        onClick={() => handleRaceHeaderClick(race)}
                                    >
                                        {getRaceAbbr(race)}
                                        {activeRaceSorts.includes(race) && activeRaceSorts.length > 1 && (
                                            <span className="sort-order-badge">{activeRaceSorts.indexOf(race) + 1}</span>
                                        )}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {processedRiders.map(rider => (
                                <RiderRow
                                    key={rider.fullName}
                                    rider={rider}
                                    onRiderClick={handleRiderClick}
                                    races={races}
                                    showFavoritesOnly={showFavoritesOnly}
                                />
                            ))}
                        </tbody>
                    </table>
                    {processedRiders.length === 0 && (
                        <div className="no-results">Geen renners gevonden.</div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default RiderOverviewModal;
